/**
 * <SocialShareButtons> — three pill buttons that open the native
 * share intent for X (Twitter), Facebook, and LinkedIn in a new tab.
 *
 * Intent URLs require no API keys and no LinkedIn product approval —
 * the user is bounced to the social site's pre-filled compose box.
 * That makes this a zero-friction, zero-risk way to turn a happy
 * in-app moment (reconciliation complete, savings goal hit, etc.)
 * into organic reach.
 *
 * Usage:
 *   <SocialShareButtons
 *     text="Just reconciled my month with Forge Ledger ⚡"
 *     url="https://forgeledger.net"
 *     onShare={(channel) => analytics.track('share', { channel })}
 *   />
 *
 * Channels are filtered through `lib/social.js` `.enabled` — if you
 * disable X there, the X pill disappears from every share strip too.
 */
import React from 'react';
import { Twitter, Facebook, Linkedin } from 'lucide-react';
import { SOCIAL } from '../lib/social';

const buildIntent = (channel, { text, url, hashtag }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  switch (channel) {
    case 'twitter': {
      const via = SOCIAL.twitter.handle.replace(/^@/, '');
      const hashtagPart = hashtag ? `&hashtags=${encodeURIComponent(hashtag)}` : '';
      return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&via=${encodeURIComponent(via)}${hashtagPart}`;
    }
    case 'facebook':
      // Facebook's sharer.php only honours the URL; the text is rendered
      // from the destination page's og:title/og:description meta tags.
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'linkedin':
      // LinkedIn's `share-offsite` endpoint pre-fills from og:* meta tags
      // on the target URL — same UX as Facebook. The text param is
      // historically ignored on LinkedIn, but we pass it for completeness.
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    default:
      return url;
  }
};

const CHANNELS = [
  {
    key: 'twitter',
    label: 'Share on X',
    Icon: Twitter,
    // X brand black (post-Twitter rebrand).
    bg: '#000000',
    hover: '#1A1A1A',
  },
  {
    key: 'facebook',
    label: 'Share on Facebook',
    Icon: Facebook,
    bg: '#1877F2',
    hover: '#1664D9',
  },
  {
    key: 'linkedin',
    label: 'Share on LinkedIn',
    Icon: Linkedin,
    bg: '#0A66C2',
    hover: '#0856A8',
  },
];

const SocialShareButtons = ({
  text,
  url,
  hashtag,
  className = '',
  size = 'default',           // 'default' | 'compact'
  testIdPrefix = 'share',
  onShare,
}) => {
  const activeChannels = CHANNELS.filter((c) => SOCIAL[c.key]?.enabled !== false);
  if (activeChannels.length === 0) return null;

  const handleClick = (channel) => {
    const target = buildIntent(channel, { text, url, hashtag });
    try {
      window.open(target, '_blank', 'noopener,noreferrer');
    } catch (_e) {
      // Pop-up blocker — fall back to top-level navigation as a courtesy.
      window.location.href = target;
    }
    if (onShare) onShare(channel);
  };

  const paddingClass = size === 'compact' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid={`${testIdPrefix}-buttons`}>
      {activeChannels.map(({ key, label, Icon, bg, hover }) => (
        <button
          key={key}
          type="button"
          onClick={() => handleClick(key)}
          className={`${paddingClass} inline-flex items-center gap-1.5 rounded-full font-medium text-white transition-colors`}
          style={{ backgroundColor: bg }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = bg; }}
          data-testid={`${testIdPrefix}-${key}-button`}
          aria-label={label}
        >
          <Icon size={size === 'compact' ? 13 : 15} aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
};

export default SocialShareButtons;
